import { $$ } from '../../../shared/js/utils/dom';
import { createFocusTrap } from 'focus-trap';
import { abbreviationTooltips } from '../tooltip/tooltip';

class Abbreviation {
  constructor(params) {
    this.element = params.abbreviation;
    this.buttonOpen = params.buttonOpen;
    this.buttonClose = params.buttonClose;
    this.dialog = params.dialog;
    this.isOpen = false;
    this.focusTrapInstance = null;
    this.viewportWidth = window.innerWidth;
    this.abbreviationTooltips = abbreviationTooltips;
    this.filteredTooltipInstances = null;
    this.uniqueStr = `${Math.floor(
      Math.random() * Math.floor(Math.random() * Date.now())
    )}`;
    this.id = `abbreviation-${this.uniqueStr}`;
  }

  removeAria() {
    this.dialog.removeAttribute('id');
    this.dialog.removeAttribute('role');
    this.dialog.removeAttribute('aria-hidden');
    this.buttonOpen.removeAttribute('aria-controls');
    this.buttonClose.removeAttribute('aria-controls');
  }

  eventUnbinding() {
    this.buttonOpen.removeEventListener('click', this.toggle);
    this.buttonClose.removeEventListener('click', this.close);
    this.buttonClose.removeEventListener('keyup', this.keyupEscHandler);
  }

  destroy() {
    this.eventUnbinding();
    this.removeAria();
    this.removeFocusTrap();
  }

  addAria() {
    this.dialog.setAttribute('id', this.id);
    this.dialog.setAttribute('role', 'dialog');
    this.dialog.setAttribute('aria-hidden', 'true');
    this.buttonOpen.setAttribute('aria-controls', this.id);
    this.buttonClose.setAttribute('aria-controls', this.id);
  }

  handleClickOutsideDialog(event) {
    if (this.isOpen && !this.element.contains(event.target)) this.close();
  }

  keyupEscHandler(event) {
    if (event.key === 'Escape') this.close();
  }

  removeFocusTrap() {
    this.focusTrapInstance?.deactivate();
  }

  addFocusTrap() {
    this.focusTrapInstance = createFocusTrap(this.dialog, {
      clickOutsideDeactivates: true,
      initialFocus: this.buttonClose,
    });
    this.focusTrapInstance.activate();
  }

  handleDialogPosition() {
    const currentPosition = this.dialog.getBoundingClientRect();
    const leftOverflow = currentPosition.x;
    const rightOverflow =
      this.viewportWidth - (currentPosition.x + currentPosition.width);

    if (leftOverflow < 0) {
      this.dialog.style.right = 0;
    }

    if (rightOverflow < 0) {
      this.dialog.style.left = `${rightOverflow}px`;
    }
  }

  open(event) {
    event?.preventDefault();

    this.isOpen = true;

    this.filteredTooltipInstances = this.abbreviationTooltips.filter(
      tooltip => tooltip.element === this.element
    );
    this.filteredTooltipInstances.forEach(tooltip => tooltip.deactivate());

    this.element.classList.add('is-open');
    this.dialog.setAttribute('aria-hidden', 'false');
    this.buttonOpen.setAttribute('aria-expanded', 'true');

    this.handleDialogPosition();
    this.addFocusTrap();
  }

  close(event) {
    event?.preventDefault();

    this.removeFocusTrap();

    this.element.classList.remove('is-open');
    this.buttonOpen.setAttribute('aria-expanded', 'false');
    this.dialog.setAttribute('aria-hidden', 'true');

    this.filteredTooltipInstances.forEach(tooltip => tooltip.activate());

    this.isOpen = false;
  }

  eventBinding() {
    this.buttonOpen.addEventListener('click', this.open.bind(this));
    this.buttonClose.addEventListener('click', this.close.bind(this));
    this.buttonOpen.addEventListener('keyup', this.keyupEscHandler.bind(this));
    document.addEventListener(
      'click',
      this.handleClickOutsideDialog.bind(this)
    );
  }

  init() {
    this.eventBinding();
    this.addAria();
  }
}

const abbreviations = $$('.abbreviation');
const abbreviationInstances = [];

abbreviations.length &&
  abbreviations.forEach(abbreviation => {
    abbreviation.removeAttribute('tabindex'); // remove attribute coming from the tooltip package

    const dialog = abbreviation.querySelector('.abbreviation__dialog');
    const buttonOpen = abbreviation.querySelector('.abbreviation__button');
    const buttonClose = abbreviation.querySelector(
      '.abbreviation__dialog-button-close'
    );

    const abbreviationInstance = new Abbreviation({
      abbreviation,
      buttonOpen,
      buttonClose,
      dialog,
    });

    abbreviationInstance.init();
    abbreviationInstances.push(abbreviationInstance);
  });
